import type { IButtonProps } from "./types";
import { Spinner } from "../spinner";
import Icon from "../icon";
import { tv } from "tailwind-variants";
import Centered from "../centered";
//import type { TStyleVariant } from '@/types/style/variant'

const ButtonClass = tv({
  base: "cursor-pointer px-5 py-2.5 text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:outline-hidden focus:ring-primary-300 text-center dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 flex items-center content-center justify-center align-center text-center transition-colors duration-150 ease-in-out",
  variants: {
    color: {
      primary: "bg-primary-500",
      primaryAlt: "bg-primary-400",
      secondary: "bg-primary-100 text-primary-500 hover:bg-primary-200",
      findex500: "bg-findex-500 hover:bg-findex-600 text-white",
      white: "bg-white text-gray-500 hover:bg-white/90",
      white900: "bg-white text-gray-900 hover:bg-grayish-100",
      whiteBlue500: "bg-white text-primary-500 hover:bg-white hover:text-primary-500 hover:opacity-80 transition-opacity",
      hoverBlue: "text-gray-500 bg-transparent border border-gray-300 hover:bg-primary-100 hover:text-primary-500 hover:border-primary-500",
      hoverBlueWhite: "text-white bg-transparent border border-white hover:bg-primary-100 hover:text-primary-500 hover:border-primary-500",
      ghostWhite: "text-white bg-transparent border border-white hover:bg-white/10",
      transparent:
        "text-gray-500 bg-transparent hover:bg-primary-100 hover:text-primary-500",
      errorInfo:
        "bg-grayish-100 text-red-500 hover:bg-red-100 border border-red-500",
      link: "bg-transparent hover:bg-transparent text-primary-500 hover:text-primary-600 p-0 focus:ring-0",
      error: "bg-red-500 hover:bg-red-600 text-white"
    },
    withIcon: {
      true: "flex items-center content-center justify-center gap-2",
    },
    onlyIcon: {
      true: "flex justify-center content-center items-center px-2.5 gap-0",
    },
    outlined: {
      true: "outline-solid outline-1 outline-gray-300 hover:outline-primary-500",
    },
    size: {
      m: "min-w-12 py-2 h-9 px-4 gap-1.5 grid-cols-[max-content_minmax(9rem,auto)] justify-center items-center align-content-center content-center",
      xs: "text-[0.25rem]",
      s: "text-xs",
      l: "py-4",
      xl: "h-12 py-3 px-5 text-base font-medium",
    },
    disabled: {
      true: "opacity-[0.25] cursor-not-allowed",
    },
    rounded: {
      true: "rounded-sm",
    },
    shadowHover: {
      true: "hover:shadow-[0px_0px_32px_4px_rgba(0,0,0,0.2)] transition-all",
    },
    selected: {
      true: "bg-primary-100 border border-primary-500 text-primary-500 hover:bg-primary-100",
    },
    noFocusRing: {
      true: "focus:ring-transparent",
    },
  },
  defaultVariants: {
    color: "primary",
  },
  compoundVariants: [
    {
      color: "primary",
      outlined: true,
      className: "border-primary-500 text-primary-500",
    },
    {
      color: "primaryAlt",
      outlined: true,
      className: "border-white text-white",
    },
  ],
});

const InnerClass = tv({
  base: "grid",
  variants: {
    withIcon: {
      true: "grid-cols-[max-content_auto] gap-2",
    },
    onlyIcon: {
      true: "flex items-center justify-center content-center align-center gap-0",
    },
    fontWeight: {
      bold: "font-bold",
      semiBold: "font-semibold",
      medium: "font-medium",
    },
  },
});

const Button = defineComponent<IButtonProps>(
  (props, ctx) => {
    const isLoading = computed(() => !!unref(props.isLoading));
    const isDisabled = computed(() => !!unref(props.disabled));

    const handleClick = (e: any) => {
      if (isDisabled.value || isLoading.value === true) return;
      if (props.onClick) {
        props.onClick(e);
      }
    };

    const placement = props.iconPlacement || "left";

    const buttonRef = ref<HTMLButtonElement | null>(null);
    const maxWidth = ref(0);

    watch(
      [isLoading],
      () => {
        if (buttonRef.value) {
          const currentWidth = buttonRef.value.offsetWidth;
          if (currentWidth > maxWidth.value) {
            maxWidth.value = currentWidth;
          }
        }
      },
      { immediate: true },
    );

    const buttonContent = computed(() => {
      if (isLoading.value) return "";
      if (ctx.slots.default)
        return (
          <Centered class={InnerClass({ fontWeight: props.fontWeight })}>
            {ctx.slots.default()}
          </Centered>
        );
      if (props.text) return <Centered>{props.text}</Centered>;
      return "";
    });

    return () => {
      if (props.iconEdge) {
        return (
          <button
            ref={buttonRef}
            onClick={handleClick}
            style={{
              whiteSpace: "nowrap",
              userSelect: "none",
            }}
            disabled={isDisabled.value}
            class={`relative ${ButtonClass({
              disabled: !!isDisabled.value,
              size: props.size as any,
              color: (props.transparent ? 'transparent' : props.variant) as any,
              outlined: !!props.outlined,
              rounded: !!props.rounded,
              shadowHover: !!props.shadowHover,
              selected: !!props.selected,
              noFocusRing: !!props.noFocusRing,
            })}`}
          >
            {props.icon && !isLoading.value && (
              <div class="absolute left-4 top-1/2 -translate-y-1/2">
                <Icon src={props.icon} />
              </div>
            )}
            <div class="w-full flex justify-center items-center">
              {isLoading.value ? (
                <Spinner class="w-4 h-4" />
              ) : (
                buttonContent.value
              )}
            </div>
          </button>
        );
      }

      return (
        <button
          ref={buttonRef}
          onClick={handleClick}
          style={{
            whiteSpace: "nowrap",
            userSelect: "none",
            ...(maxWidth.value > 0 &&
              isLoading.value && { width: `${maxWidth.value}px` }),
          }}
          disabled={isDisabled.value}
          class={ButtonClass({
            disabled: !!isDisabled.value,
            size: props.size as any,
            color: props.variant as any,
            withIcon: !!props.icon || !!isLoading.value,
            onlyIcon: !!props.onlyIcon || isLoading.value,
            outlined: !!props.outlined,
            rounded: !!props.rounded,
            shadowHover: !!props.shadowHover,
            selected: !!props.selected,
            noFocusRing: !!props.noFocusRing,
          })}
        >
          <div
            style={{
              width: "fit-content",
            }}
            class={InnerClass({
              withIcon: !!props.icon || !!isLoading.value,
              onlyIcon: !!props.onlyIcon || isLoading.value,
            })}
          >
            {props.icon && !isLoading.value && placement === "left" && (
              <Centered>
                <Icon src={props.icon} />
              </Centered>
            )}
            {isLoading.value && (
              <Centered>
                <Spinner class="w-4 h-4" />
              </Centered>
            )}
            {buttonContent.value}
            {props.icon && !isLoading.value && placement === "right" && (
              <Centered>
                <Icon src={props.icon} />
              </Centered>
            )}
          </div>
        </button>
      );
    };
  },
  {
    props: [
      "variant",
      "text",
      "label",
      "isLoading",
      "icon",
      "onlyIcon",
      "outlined",
      "size",
      "disabled",
      "transparent",
      "selected",
      "rounded",
      "onClick",
      "shadowHover",
      "noFocusRing",
      "hover",
      "fontWeight",
      "iconPlacement",
      "iconEdge",
    ],
  },
);

export default Button;
